// ogr-style.ts
// Minimal, dependency-free OGR_STYLE parser + light Mapbox mapping.
//
// Covers style parts PEN, BRUSH, SYMBOL, LABEL; multiple parts separated by ';'.
// Handles param values: colors (#RRGGBB or #RRGGBBAA), numbers with optional units,
// quoted strings, field refs {field}, booleans 0/1, small enums (cap/j/modes, etc.).
//
// Spec reference: https://gdal.org/en/stable/user/ogr_feature_style.html

import { Side } from 'three';

export type Unit = 'g' | 'px' | 'pt' | 'mm' | 'cm' | 'in';

export type ToolName = 'PEN' | 'BRUSH' | 'SYMBOL' | 'LABEL';

export interface FieldRef {
  type: 'field';
  name: string;
  unit?: Unit; // optional unit suffix after the field ref
  raw: string; // e.g. "{field}" or "{field}pt"
}

export interface ColorValue {
  type: 'color';
  hex: string; // #RRGGBB or #RRGGBBAA, normalized uppercase
  r: number;
  g: number;
  b: number;
  a: number; // a in [0,1]
}

export interface NumberWithUnit {
  type: 'number';
  value: number;
  unit?: Unit;
  raw: string;
}

export type AtomicValue =
  | ColorValue
  | NumberWithUnit
  | FieldRef
  | string // already-unquoted string literal (e.g., id name, text)
  | number // bare numeric without unit
  | boolean; // bo:1 etc.

export interface StyleParam {
  name: string; // e.g. 'c', 'w', 'p', 'fc', 't', 'f', 'cap', 'j', 'dp', 'l', etc.
  value: AtomicValue | AtomicValue[]; // 'p' can be a list like "4px 5px"
  raw: string; // original substring after ':'
}

export interface StylePart {
  tool: ToolName;
  params: StyleParam[];
  raw: string; // original "PEN(...)" including parentheses
}

export interface OgrStyleAst {
  kind: 'style';
  parts: StylePart[];
  raw: string;
}

// ---------- Parsing utilities ----------

const UNIT_RE = /(g|px|pt|mm|cm|in)\b/i;
const COLOR_RE = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i;
const FIELD_RE = /^\{([A-Za-z0-9_]+)\}(?:(g|px|pt|mm|cm|in))?$/i;

/** Split by a delimiter, but ignore delimiters inside (), "", or {}. */
function smartSplit(input: string, delimiter: string): string[] {
  const out: string[] = [];
  let buf = '';
  let depthParens = 0;
  let inQuotes = false;
  let quoteChar: '"' | "'" | null = null;
  let depthBraces = 0;

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (inQuotes) {
      buf += ch;
      if (ch === quoteChar && input[i - 1] !== '\\') {
        inQuotes = false;
        quoteChar = null;
      }
      continue;
    }
    if (ch === '"' || ch === "'") {
      inQuotes = true;
      quoteChar = ch as '"' | "'";
      buf += ch;
      continue;
    }
    if (ch === '(') depthParens++;
    if (ch === ')') depthParens = Math.max(0, depthParens - 1);
    if (ch === '{') depthBraces++;
    if (ch === '}') depthBraces = Math.max(0, depthBraces - 1);

    const atTop = depthParens === 0 && depthBraces === 0;
    if (atTop && ch === delimiter) {
      out.push(buf.trim());
      buf = '';
    } else {
      buf += ch;
    }
  }
  if (buf.trim().length) out.push(buf.trim());
  return out;
}

function unquote(s: string): string {
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    return s.slice(1, -1).replace(/\\"/g, '"').replace(/\\'/g, "'");
  }
  return s;
}

function parseColor(raw: string): ColorValue | null {
  const m = raw.match(COLOR_RE);
  if (!m) return null;
  const [_, rgb, aa] = m;
  const r = parseInt(rgb.slice(0, 2), 16);
  const g = parseInt(rgb.slice(2, 4), 16);
  const b = parseInt(rgb.slice(4, 6), 16);
  const alpha = aa ? parseInt(aa, 16) / 255 : 1;
  return {
    type: 'color',
    hex: '#' + rgb.toUpperCase() + (aa ? aa.toUpperCase() : ''),
    r,
    g,
    b,
    a: alpha,
  };
}

function parseField(raw: string): FieldRef | null {
  const m = raw.match(FIELD_RE);
  if (!m) return null;
  const [, name, u] = m;
  return {
    type: 'field',
    name,
    unit: (u ? u.toLowerCase() : undefined) as Unit | undefined,
    raw,
  };
}

function parseNumberWithOptionalUnit(raw: string): NumberWithUnit | number | null {
  // e.g., 5, 5px, 0.09g, -10pt
  const unitMatch = raw.match(UNIT_RE);
  let unit: Unit | undefined;
  let numericPart = raw;
  if (unitMatch && unitMatch.index !== undefined) {
    const idx = unitMatch.index;
    unit = unitMatch[1].toLowerCase() as Unit;
    numericPart = raw.slice(0, idx);
  }
  const num = Number(numericPart.trim());
  if (Number.isFinite(num)) {
    if (unit) return { type: 'number', value: num, unit, raw };
    // bare numeric
    return num;
  }
  return null;
}

function parseMaybeList(raw: string): AtomicValue | AtomicValue[] {
  // Used primarily for p:"4px 5px" or unquoted sequences. Accepts quoted blob or splits on whitespace.
  const trimmed = raw.trim();
  const q = (trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"));
  if (q) {
    const s = unquote(trimmed);
    const parts = s.trim().split(/\s+/).filter(Boolean);
    return parts.map((v) => parseAtomic(v));
  }
  // If not quoted, still attempt whitespace list, but only if >1 token
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length > 1) return parts.map((v) => parseAtomic(v));
  return parseAtomic(trimmed);
}

function parseAtomic(raw: string): AtomicValue {
  // order: color, field, number+unit, boolean 0/1, string
  const color = parseColor(raw);
  if (color) return color;

  const field = parseField(raw);
  if (field) return field;

  const n = parseNumberWithOptionalUnit(raw);
  if (n !== null) return n;

  if (raw === '1') return true;
  if (raw === '0') return false;

  return unquote(raw);
}

function parseParam(fragment: string): StyleParam | null {
  const idx = fragment.indexOf(':');
  if (idx === -1) return null;
  const name = fragment.slice(0, idx).trim();
  const rawVal = fragment.slice(idx + 1).trim();
  // Parameters like p may carry a space-separated list inside quotes.
  const value = name.toLowerCase() === 'p' ? parseMaybeList(rawVal) : parseAtomic(rawVal);
  return { name, value, raw: rawVal };
}

function parseParams(inner: string): StyleParam[] {
  if (!inner.trim()) return [];
  const pieces = smartSplit(inner, ',');
  const params: StyleParam[] = [];
  for (const piece of pieces) {
    const p = parseParam(piece);
    if (p) params.push(p);
  }
  return params;
}

function parsePart(raw: string): StylePart | null {
  const m = raw.match(/^([A-Za-z]+)\s*\((.*)\)$/s);
  if (!m) return null;
  const tool = m[1].toUpperCase() as ToolName;
  if (!['PEN', 'BRUSH', 'SYMBOL', 'LABEL'].includes(tool)) return null;
  const inner = m[2];
  return { tool, params: parseParams(inner), raw };
}

export function parseOgrStyle(style: string): OgrStyleAst {
  const partsRaw = smartSplit(style.trim(), ';');
  const parts: StylePart[] = [];
  for (const p of partsRaw) {
    const part = parsePart(p);
    if (part) parts.push(part);
  }
  return { kind: 'style', parts, raw: style };
}

// ---------- Unit conversion helpers (optional) ----------

export interface PixelContext {
  dpi: number; // display/print DPI
  groundUnitsPerPixel?: number; // how many "g" units per pixel (e.g., meters/pixel); if provided, converts g→px
  lat?: number;
}

export function toPixels(val: NumberWithUnit | number, ctx: PixelContext): number {
  if (typeof val === 'number') return val; // treat as px
  const { value, unit } = val;
  const dpi = ctx.dpi || 96;
  switch (unit) {
    case 'px':
      return value;
    case 'pt':
      return (value * dpi) / 72;
    case 'in':
      return value * dpi;
    case 'mm':
      return (value * dpi) / 25.4;
    case 'cm':
      return (value * dpi) / 2.54;
    case 'g':
      // if (ctx.lat) {
      return value;
    // }
    // if (ctx.groundUnitsPerPixel && ctx.groundUnitsPerPixel > 0) {
    //   return value / ctx.groundUnitsPerPixel;
    // }
    // If unknown, leave as-is; caller decides.
    // return value;
    default:
      return value;
  }
}

// ---------- Mapbox helpers (optional) ----------

export interface MapboxPaint {
  // Line properties
  'line-color'?: string;
  'line-opacity'?: number;
  'line-blur'?: number;
  'line-dasharray'?: number[];
  'line-pattern'?: string;
  'line-gap-width'?: number;
  'line-offset'?: number;
  'line-gradient'?: string;

  // Fill properties
  'fill-color'?: string;
  'fill-opacity'?: number;
  'fill-outline-color'?: string;
  'fill-pattern'?: string;
  'fill-translate'?: [number, number];
  'fill-translate-anchor'?: 'map' | 'viewport';
  'fill-antialias'?: boolean;

  // Text properties
  'text-color'?: string;
  'text-halo-color'?: string;
  'text-halo-width'?: number;
  'text-halo-blur'?: number;
  'text-opacity'?: number;
  'text-translate'?: [number, number];
  'text-translate-anchor'?: 'map' | 'viewport';

  // Icon properties
  'icon-color'?: string;
  'icon-halo-color'?: string;
  'icon-halo-width'?: number;
  'icon-halo-blur'?: number;
  'icon-opacity'?: number;
  'icon-translate'?: [number, number];
  'icon-translate-anchor'?: 'map' | 'viewport';

  // Circle properties (for point symbols)
  'circle-color'?: string;
  'circle-radius'?: number;
  'circle-blur'?: number;
  'circle-opacity'?: number;
  'circle-stroke-color'?: string;
  'circle-stroke-width'?: number;
  'circle-stroke-opacity'?: number;
  'circle-translate'?: [number, number];
  'circle-translate-anchor'?: 'map' | 'viewport';
  'circle-pitch-scale'?: 'map' | 'viewport';
  'circle-pitch-alignment'?: 'map' | 'viewport';
}

export interface MapboxLayout {
  // Text field properties
  'text-field'?: string;
  'text-font'?: string[];
  'text-size'?: number;
  'text-max-width'?: number;
  'text-line-height'?: number;
  'text-letter-spacing'?: number;
  'text-justify'?: 'auto' | 'left' | 'center' | 'right';
  'text-radial-offset'?: number;
  'text-variable-anchor'?: Array<
    'center' | 'left' | 'right' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  >;
  'text-anchor'?:
    | 'center'
    | 'left'
    | 'right'
    | 'top'
    | 'bottom'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right';
  'text-max-angle'?: number;
  'text-rotate'?: number;
  'text-padding'?: number;
  'text-keep-upright'?: boolean;
  'text-transform'?: 'none' | 'uppercase' | 'lowercase';
  'text-offset'?: [number, number];
  'text-allow-overlap'?: boolean;
  'text-ignore-placement'?: boolean;
  'text-optional'?: boolean;
  'text-writing-mode'?: Array<'horizontal' | 'vertical'>;
  'text-pitch-alignment'?: 'map' | 'viewport' | 'auto';
  'text-rotation-alignment'?: 'map' | 'viewport' | 'auto';

  // Icon properties
  'icon-image'?: string;
  'icon-size'?: number;
  'icon-rotate'?: number;
  'icon-padding'?: number;
  'icon-keep-upright'?: boolean;
  'icon-offset'?: [number, number];
  'icon-anchor'?:
    | 'center'
    | 'left'
    | 'right'
    | 'top'
    | 'bottom'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right';
  'icon-pitch-alignment'?: 'map' | 'viewport' | 'auto';
  'icon-rotation-alignment'?: 'map' | 'viewport' | 'auto';
  'icon-allow-overlap'?: boolean;
  'icon-ignore-placement'?: boolean;
  'icon-optional'?: boolean;
  'icon-text-fit'?: 'none' | 'width' | 'height' | 'both';
  'icon-text-fit-padding'?: [number, number, number, number];

  // Symbol placement
  'symbol-placement'?: 'point' | 'line' | 'line-center';
  'symbol-spacing'?: number;
  'symbol-avoid-edges'?: boolean;
  'symbol-sort-key'?: number;
  'symbol-z-order'?: 'auto' | 'viewport-y' | 'source';
}

/**
 * An OGR PEN `w:` value as a lineweight in millimetres. `g` is read as mm:
 * GDAL's DXF/DWG drivers write the lineweight in mm with a `g` unit
 * (`PEN(w:0.09g)`). Bare numbers and `px` are pixels at 96 DPI.
 */
export function penWidthToMm(width: number | NumberWithUnit): number | undefined {
  if (typeof width === 'number') return (width * 25.4) / 96;
  const { value, unit } = width;
  if (!Number.isFinite(value)) return undefined;
  switch (unit) {
    case 'g':
    case 'mm':
      return value;
    case 'cm':
      return value * 10;
    case 'in':
      return value * 25.4;
    case 'pt':
      return (value * 25.4) / 72;
    case 'px':
    default:
      return (value * 25.4) / 96;
  }
}

export interface MapboxStyle {
  paint: MapboxPaint;
  layout: MapboxLayout;
  /**
   * The PEN's lineweight in mm (`penWidthToMm`): undefined with no PEN, null
   * for a PEN that sets no width.
   */
  lineweightMm?: number | null;
}

const EQUATOR_METERS_PER_PIXEL_Z0 = 156543.03392; // meters per pixel at z=0, φ=0°
const MAX_MERCATOR_LAT = 85.05112878;

/**
 * Clamp latitude to the Web Mercator valid range.
 */
function clampLat(latDeg: number): number {
  return Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, latDeg));
}

/**
 * DEN(φ) = 156543.03392 * cos(φ), with φ in radians
 * s(z, φ) = 2^z / DEN(φ)
 */
function denom(latDeg: number): number {
  const φ = (clampLat(latDeg) * Math.PI) / 180;
  return EQUATOR_METERS_PER_PIXEL_Z0 * Math.cos(φ);
}

/**
 * Scale at zoom 0 (s0) and at zoom 22 (s22) for a given latitude in degrees.
 * s0 = 1 / DEN(φ)
 * s22 = 2^24 / DEN(φ)
 */
export function s0(latDeg: number): number {
  return 1 / denom(latDeg);
}

export function s22(latDeg: number): number {
  return Math.pow(2, 22) / denom(latDeg);
}

/**
 * General scale for any zoom.
 * Useful if you want to precompute stops for other zooms.
 */
export function sAtZoom(latDeg: number, z: number): number {
  return Math.pow(2, z) / denom(latDeg);
}

/**
 * Convert a parsed OGR style to a minimal Mapbox style mapping.
 * Does not perform g→px conversions unless ctx.groundUnitsPerPixel is provided.
 */
export function toMapbox(ast: OgrStyleAst, ctx: PixelContext = { dpi: 96 }): MapboxStyle {
  const paint: MapboxPaint = {};
  const layout: MapboxLayout = {};
  let lineweightMm: number | null | undefined;

  for (const part of ast.parts) {
    if (part.tool === 'PEN') {
      let color: ColorValue | undefined;
      let width: number | NumberWithUnit | undefined;
      let alpha: number | undefined;

      for (const p of part.params) {
        if (p.name.toLowerCase() === 'c') {
          if (typeof p.value === 'string') {
            const c = parseColor(p.value);
            if (c) {
              color = c;
              alpha = c.a;
            }
          } else if (typeof p.value === 'object' && (p.value as any).type === 'color') {
            color = p.value as ColorValue;
            alpha = color.a;
          }
        }
        if (p.name.toLowerCase() === 'w') {
          if (typeof p.value === 'number') width = p.value;
          else if (typeof p.value === 'object' && (p.value as any).type === 'number') width = p.value as NumberWithUnit;
          else if (typeof p.value === 'string') {
            const maybe = parseNumberWithOptionalUnit(p.value);
            if (maybe !== null) width = maybe as any;
          }
        }
      }
      if (color) {
        paint['line-color'] = '#' + [color.r, color.g, color.b].map((v) => v.toString(16).padStart(2, '0')).join('');
        if (alpha !== undefined) paint['line-opacity'] = alpha;
      }
      // No `paint['line-width']`: a CAD pen's `g` width is mm, which only the
      // map's lineweight mode can turn into pixels (lib/mapLineweight.ts).
      lineweightMm = width !== undefined ? (penWidthToMm(width) ?? null) : null;
    }

    if (part.tool === 'BRUSH') {
      let fc: ColorValue | undefined; // fill color
      let bc: ColorValue | undefined; // background; used as outline fallback

      for (const p of part.params) {
        if (p.name.toLowerCase() === 'fc' || p.name.toLowerCase() === 'c') {
          // Spec uses 'fc' for forecolor; some sources use 'c' for simple fills.
          const v = p.value;
          const c =
            typeof v === 'string'
              ? parseColor(v)
              : typeof v === 'object' && (v as any).type === 'color'
                ? (v as ColorValue)
                : null;
          if (c) fc = c;
        }
        if (p.name.toLowerCase() === 'bc') {
          const v = p.value;
          const c =
            typeof v === 'string'
              ? parseColor(v)
              : typeof v === 'object' && (v as any).type === 'color'
                ? (v as ColorValue)
                : null;
          if (c) bc = c;
        }
      }
      if (fc) {
        paint['fill-color'] = '#' + [fc.r, fc.g, fc.b].map((v) => v.toString(16).padStart(2, '0')).join('');
        paint['fill-opacity'] = fc.a;
      }
      if (bc) {
        // Mapbox has no "background to pattern" concept; safest mapping is outline color.
        paint['fill-outline-color'] = '#' + [bc.r, bc.g, bc.b].map((v) => v.toString(16).padStart(2, '0')).join('');
      }
    }

    if (part.tool === 'LABEL') {
      // Minimal mapping: text string (t), size (s), color (c), angle (a), font (f)
      for (const p of part.params) {
        const key = p.name.toLowerCase();
        if (key === 't') {
          const { plain: plainText, paragraphs } = parseMText(p.value);
          // todo: implement other paragraph options
          if (paragraphs[0]?.props?.indentFirstRelToLeft) {
            layout['text-offset'] = [paragraphs[0].props.indentFirstRelToLeft, 0];
          }
          // If it's {field}, Mapbox text-field expects a token-like expression. Leave raw.
          if (typeof p.value === 'object' && (p.value as any).type === 'field') {
            layout['text-field'] = ['get', (p.value as FieldRef).name] as any; // for runtime expressions users can adjust
          } else if (typeof p.value === 'string') {
            layout['text-field'] = plainText;
          }
        }
        if (key === 's') {
          const v = p.value;
          let px: number | null = null;
          if (typeof v === 'number') px = v;
          else if (typeof v === 'object' && (v as any).type === 'number') {
            px = toPixels(v as NumberWithUnit, ctx);
          } else if (typeof v === 'string') {
            const n = parseNumberWithOptionalUnit(v);
            if (n !== null) px = typeof n === 'number' ? n : toPixels(n as NumberWithUnit, ctx);
          }
          if (px !== null) layout['text-size'] = px;
        }
        if (key === 'a') {
          const v = p.value;
          const deg =
            typeof v === 'number'
              ? v
              : typeof v === 'object' && (v as any).type === 'number'
                ? (v as NumberWithUnit).value
                : NaN;
          if (Number.isFinite(deg)) layout['text-rotate'] = -deg as number;
        }
        if (key === 'c') {
          // Text color
          const v = p.value;
          const c =
            typeof v === 'string'
              ? parseColor(v)
              : typeof v === 'object' && (v as any).type === 'color'
                ? (v as ColorValue)
                : null;
          if (c) {
            paint['text-color'] = '#' + [c.r, c.g, c.b].map((v) => v.toString(16).padStart(2, '0')).join('');
            if (c.a < 1) paint['text-opacity'] = c.a;
          }
        }
        if (key === 'o') {
          // Outline/halo color
          const v = p.value;
          const c =
            typeof v === 'string'
              ? parseColor(v)
              : typeof v === 'object' && (v as any).type === 'color'
                ? (v as ColorValue)
                : null;
          if (c) {
            paint['text-halo-color'] = '#' + [c.r, c.g, c.b].map((v) => v.toString(16).padStart(2, '0')).join('');
            // Set a default halo width if not specified
            if (!paint['text-halo-width']) paint['text-halo-width'] = 1;
          }
        }
        if (key === 'f') {
          // Font family list; Mapbox expects an array of font names under layout "text-font".
          const val =
            typeof p.value === 'string' ? p.value : Array.isArray(p.value) ? p.value.join(',') : String(p.value);
          const families = val
            .split(',')
            .map((s: string) => unquote(s.trim()))
            .filter(Boolean);
          layout['text-font'] = families;
        }
        if (key === 'p') {
          // Anchor position 1..12 → text-anchor mapping
          const n = Number(String(p.value).trim());
          const anchorMap: Record<number, MapboxLayout['text-anchor']> = {
            1: 'bottom-left',
            2: 'bottom',
            3: 'bottom-right',
            4: 'left',
            5: 'center',
            6: 'right',
            7: 'top-left',
            8: 'top',
            9: 'top-right',
            10: 'bottom-left',
            11: 'bottom',
            12: 'bottom-right',
          };
          if (anchorMap[n]) {
            layout['text-anchor'] = anchorMap[n];
          }
        }
        if (key === 'dx' || key === 'dy') {
          // Text offset
          const offset = layout['text-offset'] || [0, 0];
          if (key === 'dx') {
            const val =
              typeof p.value === 'number'
                ? p.value
                : typeof p.value === 'object' && (p.value as any).type === 'number'
                  ? toPixels(p.value as NumberWithUnit, ctx)
                  : 0;
            offset[0] = val;
          }
          if (key === 'dy') {
            const val =
              typeof p.value === 'number'
                ? p.value
                : typeof p.value === 'object' && (p.value as any).type === 'number'
                  ? toPixels(p.value as NumberWithUnit, ctx)
                  : 0;
            offset[1] = val;
          }
          layout['text-offset'] = offset as [number, number];
        }
        if (key === 'bo') {
          // Bold - can be handled via font selection in text-font array
          const isBold = p.value === true || p.value === 1 || String(p.value) === '1';
          if (isBold && layout['text-font']) {
            // Attempt to modify font names to bold variants
            layout['text-font'] = layout['text-font'].map((font) => (font.includes('Bold') ? font : `${font} Bold`));
          }
        }
      }
    }

    if (part.tool === 'SYMBOL') {
      // Mapbox "icon-image" typically maps to an atlas key; we surface 'id' and 's' (size).
      for (const p of part.params) {
        const key = p.name.toLowerCase();
        if (key === 'id') {
          const v = Array.isArray(p.value) ? p.value[0] : p.value;
          if (typeof v === 'string') layout['icon-image'] = v;
        }
        if (key === 's') {
          const v = p.value;
          let px: number | null = null;
          if (typeof v === 'number') px = v;
          else if (typeof v === 'object' && (v as any).type === 'number') px = toPixels(v as NumberWithUnit, ctx);
          else if (typeof v === 'string') {
            const n = parseNumberWithOptionalUnit(v);
            if (n !== null) px = typeof n === 'number' ? n : toPixels(n as NumberWithUnit, ctx);
          }
          if (px !== null) layout['text-size'] = px; // or icon-size via expression, left to consumer
        }
      }
    }
  }

  return { paint, layout, lineweightMm };
}

// Minimal mapper from parsed OGR style (from ogr-style.ts) to Three.js MeshBasicMaterial params.
// No Three.js import required; returns a plain POJO compatible with THREE.MeshBasicMaterialParameters.
//
// Policy:
// - Use BRUSH forecolor (fc or c) for mesh base color. Alpha -> opacity/transparent.
// - If no BRUSH, fall back to PEN color.
// - Optional: set wireframe=true when a PEN exists (since OGR PEN implies stroke/outline).
//   Note: MeshBasicMaterial uses the same color for faces and wireframe; to draw a colored
//   outline with a different color/width, create a separate Line{,Segments} + LineBasicMaterial.
// - LABEL/SYMBOL are ignored for MeshBasicMaterial.
// - Side can be provided by the caller; we do not reference THREE.Side constants here.

export interface MeshBasicMaterialParamsLike {
  color?: number; // 0xRRGGBB
  opacity?: number; // 0..1
  transparent?: boolean; // true if opacity < 1
  wireframe?: boolean; // optional, default false
  side?: Side; // pass-through for THREE.FrontSide/BackSide/DoubleSide
}

export interface ToThreeOptions {
  /** If true, set wireframe=true when a PEN part is present. Default: false. */
  wireframeFromPen?: boolean;
  /** Override base color if style provides none. Default: 0xffffff. */
  fallbackColor?: number;
  /**
   * Optional side constant; pass THREE.FrontSide / BackSide / DoubleSide here.
   * We do not import THREE to keep this file dependency-free.
   */
  side?: Side;
}

/** Convert a ColorValue to 0xRRGGBB integer. */
function colorToHexNumber(c: ColorValue): number {
  const n = (c.r << 16) | (c.g << 8) | c.b;
  return n >>> 0;
}

function firstPart(ast: OgrStyleAst, tool: 'BRUSH' | 'PEN'): StylePart | undefined {
  return ast.parts.find((p) => p.tool === tool);
}

/**
 * Extract the best fill color + alpha from the style.
 * Prefers BRUSH fc/c; falls back to PEN c; returns undefined if none present.
 */
function extractFillColor(ast: OgrStyleAst): ColorValue | undefined {
  const brush = firstPart(ast, 'BRUSH');
  if (brush) {
    for (const p of brush.params) {
      const key = p.name.toLowerCase();
      if (key === 'fc' || key === 'c') {
        const v = p.value as any;
        if (v && typeof v === 'object' && v.type === 'color') return v as ColorValue;
        if (typeof p.raw === 'string') {
          // p.raw already holds the original token; parseAtomic in ogr-style.ts would have resolved it.
          const pv = p.value as any;
          if (pv && typeof pv === 'object' && pv.type === 'color') return pv as ColorValue;
        }
      }
    }
  }
  const pen = firstPart(ast, 'PEN');
  if (pen) {
    for (const p of pen.params) {
      if (p.name.toLowerCase() === 'c') {
        const v = p.value as any;
        if (v && typeof v === 'object' && v.type === 'color') return v as ColorValue;
      }
    }
  }
  return undefined;
}

/**
 * Produce plain MeshBasicMaterial-like parameters from an OGR style AST.
 * Does not mutate the AST; does not instantiate THREE.MeshBasicMaterial.
 */
export function toThreeMeshBasicParams(ast: OgrStyleAst, opts: ToThreeOptions = {}): MeshBasicMaterialParamsLike {
  const { wireframeFromPen = false, fallbackColor = 0xffffff, side } = opts;

  const out: MeshBasicMaterialParamsLike = {};

  const brushColor = extractFillColor(ast);
  if (brushColor) {
    out.color = colorToHexNumber(brushColor);
    const a = brushColor.a;
    if (a < 1) {
      out.opacity = a;
      out.transparent = true;
    }
  } else {
    out.color = fallbackColor;
  }

  if (wireframeFromPen) {
    const pen = firstPart(ast, 'PEN');
    if (pen) out.wireframe = true;
  }

  if (typeof side !== 'undefined') out.side = side;

  return out;
}

// OGR LABEL(...) -> @react-three/drei <Text> props (Troika SDF)
// Mapped from the spec’s “Label Tool Parameters”. See notes inline.
// Ref: GDAL OGR Feature Style — LABEL parameters. Troika/Drei text API.
// Docs: https://gdal.org/.../ogr_feature_style.html#label-tool-parameters
//       troika-three-text props (used by <Text /> from drei)
//
// Supported LABEL keys: f, s, t, a, c, o, h, w, m, p, dx, dy, dp, bo, it, un, st, l
//   f  -> font (comma list or URL)
//   s  -> fontSize (unit aware)
//   t  -> text (string or {field})
//   a  -> rotation (deg → rad about Z)
//   c  -> color (alpha ignored by Troika color; opacity not handled here)
//   o  -> outlineColor (+ outlineOpacity from AA); width via option
//   h  -> shadow color (no Troika “text shadow”; surfaced in userData)
//   w  -> stretch factor % (no Troika glyph stretch; surfaced in userData)
//   m  -> placement mode (line-based; surfaced in userData)
//   p  -> anchor position 1..12 → anchorX/anchorY (heuristic mapping; see table)
//   dx,dy -> offset (unit aware); surfaced as positionOffset [x,y,0]
//   dp -> perpendicular offset for line labels; surfaced in userData
//   bo,it,un,st -> bold/italic/underline/strikethrough; map bo→fontWeight, it→fontStyle; un/st surfaced in userData
//   l  -> priority; surfaced in userData
//
// Limitations:
// - No native background box in Troika; LABEL b: is not part of the spec (b is “background box” in older notes for some UIs),
//   but GDAL spec defines b as “Text Background Color (filled box)” — Troika has no built-in. Surface to userData.
// - Alpha for main text color is not applied to material here. If needed, set material.opacity on the Text’s material.
// - Placement modes m:(p|l|s|m|w|h|a) are not performed here.
//
// Troika props reference: outlineWidth/Color/Opacity, stroke*, textAlign, anchorX/Y, font, fontSize, color.
// Spec reference for LABEL parameters.   [oai_citation:0‡gdal.org](https://gdal.org/en/stable/user/ogr_feature_style.html)

export type TextAlign = 'left' | 'right' | 'center' | 'justify';

export interface DreiTextPropsLike {
  text?: string;
  color?: string | number;
  font?: string;
  fontSize?: number;
  textAlign?: TextAlign;
  fontWeight?: string | number; // 'bold' when bo:1
  fontStyle?: 'normal' | 'italic';
  outlineWidth?: number | string; // e.g. 0.04 or '10%'
  outlineColor?: string | number;
  outlineOpacity?: number;

  // Drei/Troika:
  rotation?: [number, number, number]; // radians
  anchorX?: 'left' | 'center' | 'right' | number;
  anchorY?: 'top' | 'top-baseline' | 'middle' | 'bottom' | 'bottom-baseline' | number;

  // Not native props; for the caller to apply:
  positionOffset?: [number, number, number];

  // Pass-through metadata for anything we can’t natively render:
  userData?: {
    ogr?: Record<string, unknown>;
  };
}

// ——— helpers ———

function degToRad(d: number): number {
  return (d * Math.PI) / 180;
}

function rgbToHexString(c: ColorValue): string {
  return '#' + [c.r, c.g, c.b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

function firstLabel(ast: OgrStyleAst): StylePart | undefined {
  return ast.parts.find((p) => p.tool === 'LABEL');
}

function toPixelsLocal(val: NumberWithUnit | number, ctx: PixelContext): number | null {
  // Same conversion policy as your existing toPixels()
  const { dpi = 96, groundUnitsPerPixel } = ctx;
  if (typeof val === 'number') return val;
  const { value, unit } = val;
  switch (unit) {
    case 'px':
      return value;
    case 'pt':
      return (value * dpi) / 72;
    case 'in':
      return value * dpi;
    case 'mm':
      return (value * dpi) / 25.4;
    case 'cm':
      return (value * dpi) / 2.54;
    case 'g':
      if (groundUnitsPerPixel && groundUnitsPerPixel > 0) return value / groundUnitsPerPixel;
      return value; // treat as scene/world units
    default:
      return value;
  }
}

// p: 1..12 “Anchor Position” → (anchorX, anchorY)
// Four rows (baseline, center, top, bottom) × three cols (left, center, right).
// Heuristic mapping inferred from the spec description and image:
//
//  1  2  3   => baseline: L C R  -> anchorY='bottom-baseline'
//  4  5  6   => center:   L C R  -> 'middle'
//  7  8  9   => top:      L C R  -> 'top-baseline'
// 10 11 12   => bottom:   L C R  -> 'bottom'
//
// If a driver interprets the grid slightly differently, adjust here.   [oai_citation:1‡gdal.org](https://gdal.org/en/stable/user/ogr_feature_style.html)
const P_TO_ANCHOR: Record<number, { x: 'left' | 'center' | 'right'; y: DreiTextPropsLike['anchorY'] }> = {
  1: { x: 'left', y: 'bottom-baseline' },
  2: { x: 'center', y: 'bottom-baseline' },
  3: { x: 'right', y: 'bottom-baseline' },
  4: { x: 'left', y: 'middle' },
  5: { x: 'center', y: 'middle' },
  6: { x: 'right', y: 'middle' },
  7: { x: 'left', y: 'top-baseline' },
  8: { x: 'center', y: 'top-baseline' },
  9: { x: 'right', y: 'top-baseline' },
  10: { x: 'left', y: 'bottom' },
  11: { x: 'center', y: 'bottom' },
  12: { x: 'right', y: 'bottom' },
};

function ensureUserData(out: DreiTextPropsLike) {
  if (!out.userData) out.userData = {};
  if (!out.userData.ogr) out.userData.ogr = {};
  return out.userData.ogr as Record<string, unknown>;
}

// ——— main ———

export interface ToDreiTextOptions {
  fallbackText?: string;
  defaultOutlineWidth?: number | string; // when LABEL o: is present but no explicit width exists in OGR
  ctx?: PixelContext;
}

/** LABEL(...) -> Drei/Troika Text props */
export function toDreiTextProps(ast: OgrStyleAst, opts: ToDreiTextOptions = {}): DreiTextPropsLike {
  const { fallbackText = '', defaultOutlineWidth = '10%', ctx = { dpi: 96 } } = opts;

  const out: DreiTextPropsLike = {};
  const meta = ensureUserData(out);

  const label = firstLabel(ast);
  if (!label) return out;

  for (const p of label.params) {
    const key = p.name.toLowerCase();
    const v = p.value as any;

    if (key === 't') {
      if (v && typeof v === 'object' && v.type === 'field') {
        out.text = `{${(v as FieldRef).name}}`;
      } else {
        out.text = String(v ?? fallbackText);
      }
    }

    if (key === 's') {
      let size: number | null = null;
      if (typeof v === 'number') size = v;
      else if (v && typeof v === 'object' && v.type === 'number') size = toPixelsLocal(v as NumberWithUnit, ctx);
      if (size != null) out.fontSize = size;
    }

    if (key === 'a') {
      const deg =
        typeof v === 'number'
          ? v
          : v && typeof v === 'object' && v.type === 'number'
            ? (v as NumberWithUnit).value
            : NaN;
      if (Number.isFinite(deg)) out.rotation = [0, 0, degToRad(deg as number)];
    }

    if (key === 'c') {
      // Text forecolor
      if (v && typeof v === 'object' && v.type === 'color') {
        out.color = rgbToHexString(v as ColorValue);
        // v.a ignored; apply opacity via material if desired.
      } else if (typeof v === 'string') {
        out.color = v;
      }
    }

    // if (key === 'f') {
    //   const s = Array.isArray(p.value)
    //     ? (p.value as unknown[]).join(',')
    //     : String(p.value);
    //   out.font = s;
    // }

    if (key === 'o') {
      // Outline/halo
      if (v && typeof v === 'object' && v.type === 'color') {
        const col = v as ColorValue;
        out.outlineColor = rgbToHexString(col);
        out.outlineOpacity = col.a;
        if (out.outlineWidth == null) out.outlineWidth = defaultOutlineWidth;
      } else if (typeof v === 'string') {
        out.outlineColor = v;
        if (out.outlineWidth == null) out.outlineWidth = defaultOutlineWidth;
      }
    }

    if (key === 'h') {
      // Shadow color — no native Troika "text shadow". Surface for consumers.
      meta.h =
        v && typeof v === 'object' && v.type === 'color'
          ? { color: rgbToHexString(v as ColorValue), a: (v as ColorValue).a }
          : v;
    }

    if (key === 'w') {
      // Stretch factor % (100=normal). No Troika glyph stretch.
      meta.stretchPercent =
        typeof v === 'number' ? v : v && typeof v === 'object' && v.type === 'number' ? (v as NumberWithUnit).value : v;
    }

    if (key === 'm') {
      // Placement mode along line geometry; not enacted here.
      meta.mode = v;
    }

    if (key === 'p') {
      const n = Number(String(v).trim());
      const map = P_TO_ANCHOR[n as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12];
      if (map) {
        out.anchorX = map.x;
        out.anchorY = map.y!;
      } else {
        meta.anchorP = v;
      }
    }

    if (key === 'dx' || key === 'dy') {
      const nx =
        key === 'dx'
          ? typeof v === 'number'
            ? v
            : v?.type === 'number'
              ? toPixelsLocal(v as NumberWithUnit, ctx)
              : null
          : null;
      const ny =
        key === 'dy'
          ? typeof v === 'number'
            ? v
            : v?.type === 'number'
              ? toPixelsLocal(v as NumberWithUnit, ctx)
              : null
          : null;
      const cur = out.positionOffset ?? [0, 0, 0];
      out.positionOffset = [cur[0] + (nx ?? 0), cur[1] + (ny ?? 0), cur[2]];
    }

    if (key === 'dp') {
      // Perpendicular offset relative to a line; not enacted here.
      const val = typeof v === 'number' ? v : v?.type === 'number' ? toPixelsLocal(v as NumberWithUnit, ctx) : v;
      meta.dp = val;
    }

    if (key === 'bo') {
      const on = v === true || v === 1 || String(v) === '1';
      if (on) out.fontWeight = 'bold';
    }
    if (key === 'it') {
      const on = v === true || v === 1 || String(v) === '1';
      if (on) out.fontStyle = 'italic';
    }
    if (key === 'un') {
      const on = v === true || v === 1 || String(v) === '1';
      meta.underline = on;
    }
    if (key === 'st') {
      const on = v === true || v === 1 || String(v) === '1';
      meta.strikethrough = on;
    }

    if (key === 'l') {
      meta.priority = typeof v === 'number' ? v : Number(v);
    }
  }

  if (out.text == null) out.text = fallbackText;
  return out;
}

export type Align = 'bottom' | 'center' | 'top';

export interface FontSpec {
  name: string; // e.g., "Arial" or SHX name
  bold?: boolean; // |b1
  italic?: boolean; // |i1
  codePage?: number; // |c238
  pitch?: number; // |p10
}

export interface Style {
  underline?: boolean;
  overline?: boolean;
  strike?: boolean;
  colorIndex?: number; // \C1..255
  height?: number; // absolute points/units
  heightRel?: number; // relative multiplier (x suffix)
  widthFactor?: number; // absolute
  widthFactorRel?: number; // relative multiplier (x suffix)
  tracking?: number; // absolute
  trackingRel?: number; // relative multiplier
  oblique?: number; // degrees from \Q
  font?: FontSpec; // \f or \F
}

export interface TextRun {
  kind: 'text';
  text: string;
  style: Style;
}

export type StackDivider = 'horizontal' | 'diagonal' | 'tolerance';

export interface StackRun {
  kind: 'stack';
  top: string;
  bottom: string;
  divider: StackDivider; // "/" => horizontal; "#" => diagonal; "^" => tolerance-style
  style: Style;
}

export type Run = TextRun | StackRun;

export interface ParagraphProps {
  // "\pi" = first-line indent relative to "\pl" (may be negative for hanging)
  indentFirstRelToLeft?: number; // e.g., -5, 1.5
  // "\pl" = left indent
  left?: number;
  // "\pr" = right indent
  right?: number;
  // raw string kept for unsupported flags (e.g., q, t, resets)
  raw?: string;
}

// in Paragraph
export interface Paragraph {
  runs: Run[];
  align?: Align;
  rawProps?: string; // keep for backward compat
  props?: ParagraphProps; // structured parse
}

export interface MTextDoc {
  paragraphs: Paragraph[];
  plain: string; // concatenated text without formatting
}

/**
 * Parse an AutoCAD MTEXT content string into paragraphs and styled runs.
 * Supported codes: \L \l \O \o \K \k \P \p \X \Q \H \W \T \F|\f \S \A \C \~ \\ \{ \} and tabs.
 * Grouping with { ... } applies scoped style overrides. Nested up to 8 levels.
 *
 * References:
 * - CADforum “Text formatting codes in MText objects” (Tip 8640).
 * - ezdxf MText inline codes list.
 * - Autodesk DevBlog: “Dissecting MTEXT Format Codes” (font traits |b |i |c |p).
 */
export function parseMText(input: string): MTextDoc {
  let i = 0;

  const baseStyle: Style = {};
  const styleStack: Style[] = [cloneStyle(baseStyle)];
  let para: Paragraph = { runs: [] };
  const paragraphs: Paragraph[] = [para];

  function curStyle(): Style {
    return styleStack[styleStack.length - 1];
  }

  function pushStyle(mod: (s: Style) => void) {
    const s = cloneStyle(curStyle());
    mod(s);
    styleStack.push(s);
  }
  function popStyle() {
    if (styleStack.length > 1) styleStack.pop();
  }

  function emitText(text: string) {
    if (!text) return;
    const last = para.runs[para.runs.length - 1];
    if (last && last.kind === 'text' && shallowStyleEq(last.style, curStyle())) {
      last.text += text;
    } else {
      para.runs.push({ kind: 'text', text, style: cloneStyle(curStyle()) });
    }
  }

  function newParagraph() {
    // Start a new paragraph inheriting current style
    const p: Paragraph = { runs: [] };
    paragraphs.push(p);
    // Note: alignment is paragraph-scoped; we'll set when we encounter \A*
    para as any; // TS hush; we switch variable next
    Object as any; // hush
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  }

  // Helpers
  function readUntilSemicolon(): string {
    const start = i;
    while (i < input.length && input[i] !== ';') i++;
    const s = input.slice(start, i);
    if (input[i] === ';') i++; // consume ';'
    return s;
  }

  function readNumberMaybeRel(): { value: number; rel: boolean } | null {
    // number possibly ending with 'x' to indicate relative multiplier
    const token = readUntilSemicolon().trim();
    if (!token) return null;
    const rel = token.endsWith('x') || token.endsWith('X');
    const numStr = rel ? token.slice(0, -1) : token;
    const v = Number(numStr);
    if (!isFinite(v)) return null;
    return { value: v, rel };
  }

  function parseFontSpec(payload: string): FontSpec {
    // payload like: "Arial|b1|i0|c238|p10" or "gdt;o"
    const [nameRaw, ...opts] = payload.split('|');
    const name = nameRaw.trim();
    const f: FontSpec = { name };
    for (const opt of opts) {
      const k = opt[0]?.toLowerCase();
      const v = opt.slice(1);
      switch (k) {
        case 'b':
          f.bold = v === '1';
          break;
        case 'i':
          f.italic = v === '1';
          break;
        case 'c': {
          const n = parseInt(v, 10);
          if (Number.isFinite(n)) f.codePage = n;
          break;
        }
        case 'p': {
          const n = parseInt(v, 10);
          if (Number.isFinite(n)) f.pitch = n;
          break;
        }
        default:
          break;
      }
    }
    return f;
  }

  function parseStack(payload: string): StackRun | null {
    // Accept forms documented: "A^ B", "X/Y", "1#4"
    // We'll detect first occurrence of '^' or '/' or '#'
    // Tolerance "^" form often has spaces after caret to avoid decoding; we trim.
    const raw = payload.trim();
    let divider: StackDivider | null = null;
    let idx = raw.indexOf('^');
    if (idx >= 0) divider = 'tolerance';
    else {
      idx = raw.indexOf('/');
      if (idx >= 0) divider = 'horizontal';
      else {
        idx = raw.indexOf('#');
        if (idx >= 0) divider = 'diagonal';
      }
    }
    if (!divider || idx < 0) return null;
    const top = raw.slice(0, idx).trim();
    const bottom = raw.slice(idx + 1).trim();
    return { kind: 'stack', top, bottom, divider, style: cloneStyle(curStyle()) };
  }

  function setAlignFromA(n: number) {
    // \A0 bottom, \A1 center, \A2 top
    const a: Align | undefined = n === 0 ? 'bottom' : n === 1 ? 'center' : n === 2 ? 'top' : undefined;
    if (a) para.align = a;
  }

  while (i < input.length) {
    const ch = input[i++];

    if (ch === '\\') {
      if (i >= input.length) {
        emitText('\\');
        break;
      }
      const code = input[i++];

      switch (code) {
        case '\\':
          emitText('\\');
          break; // escaped backslash
        case '{':
          emitText('{');
          break; // escaped literal
        case '}':
          emitText('}');
          break;
        case '~':
          emitText('\u00A0');
          break; // non-breaking space

        case 'L':
          pushStyle((s) => (s.underline = true));
          break;
        case 'l':
          pushStyle((s) => (s.underline = false));
          break;
        case 'O':
          pushStyle((s) => (s.overline = true));
          break;
        case 'o':
          pushStyle((s) => (s.overline = false));
          break;
        case 'K':
          pushStyle((s) => (s.strike = true));
          break;
        case 'k':
          pushStyle((s) => (s.strike = false));
          break;

        case 'P': {
          // new paragraph
          // Finish current paragraph, start a new one
          paragraphs[paragraphs.length - 1]; // hush
          // Start new paragraph; style context carries on implicitly by design of MTEXT editor,
          // but paragraph aligns/properties reset per paragraph.
          // We'll keep the current style stack; only the paragraph container changes.
          const p: Paragraph = { runs: [] };
          paragraphs.push(p);
          // Redirect 'para' reference by mutating (closure) variable
          (para as any).runs = para.runs; // keep TS happy; we don't reassign const in TS
          // Hack: emulate "rebind" by using Object.defineProperty
          Object.defineProperty(globalThis, '__MT_PARA', { value: p, writable: true });
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          para = (globalThis as any).__MT_PARA; // rebind
          break;
        }

        case 'p': {
          const payload = readUntilSemicolon(); // e.g., "i-5,l5" or "i*,l*,r*,q*,t"
          para.rawProps = payload;

          const props: ParagraphProps = { raw: payload };
          // tokens separated by comma; each is like "i-5", "l5", "r1.2", or "i*"
          for (const tokRaw of payload.split(',')) {
            const tok = tokRaw.trim();
            if (!tok) continue;
            const key = tok[0].toLowerCase();
            const valStr = tok.slice(1);
            if (valStr === '*') {
              // reset -> drop the specific field
              if (key === 'i') delete props.indentFirstRelToLeft;
              else if (key === 'l') delete props.left;
              else if (key === 'r') delete props.right;
              continue;
            }
            const num = Number(valStr);
            if (!Number.isFinite(num)) continue;
            if (key === 'i') props.indentFirstRelToLeft = num;
            else if (key === 'l') props.left = num;
            else if (key === 'r') props.right = num;
          }
          para.props = props;
          break;
        }

        case 'X': {
          // Paragraph wrap marker on dimension line — we just keep a placeholder text
          emitText('\u200B'); // zero-width space placeholder
          break;
        }

        case 'Q': {
          const n = readNumberMaybeRel();
          if (n) pushStyle((s) => (s.oblique = n.value));
          break;
        }

        case 'H': {
          const n = readNumberMaybeRel();
          if (n)
            pushStyle((s) => {
              if (n.rel) s.heightRel = n.value;
              else s.height = n.value;
            });
          break;
        }

        case 'W': {
          const n = readNumberMaybeRel();
          if (n)
            pushStyle((s) => {
              if (n.rel) s.widthFactorRel = n.value;
              else s.widthFactor = n.value;
            });
          break;
        }

        case 'T': {
          const n = readNumberMaybeRel();
          if (n)
            pushStyle((s) => {
              if (n.rel) s.trackingRel = n.value;
              else s.tracking = n.value;
            });
          break;
        }

        case 'F':
        case 'f': {
          const payload = readUntilSemicolon();
          const font = parseFontSpec(payload);
          pushStyle((s) => (s.font = font));
          break;
        }

        case 'S': {
          const payload = readUntilSemicolon();
          const stack = parseStack(payload);
          if (stack) para.runs.push(stack);
          break;
        }

        case 'A': {
          const num = readUntilSemicolon().trim();
          const n = Number(num);
          if (Number.isFinite(n)) setAlignFromA(n);
          break;
        }

        case 'C':
        case 'c': {
          const num = readUntilSemicolon().trim();
          const n = Number(num);
          if (Number.isFinite(n)) pushStyle((s) => (s.colorIndex = n));
          break;
        }

        case 't': {
          // tab character (not always documented in the same table but works in practice)
          emitText('\t');
          break;
        }

        default: {
          // Unknown code — emit literally
          emitText('\\' + code);
          break;
        }
      }
      continue;
    }

    if (ch === '{') {
      // Start a grouping scope: push a style frame with no change
      pushStyle((s) => s);
      continue;
    }
    if (ch === '}') {
      popStyle();
      continue;
    }

    // Regular char
    emitText(ch);
  }

  function dividerChar(d: StackDivider): string {
    switch (d) {
      case 'horizontal':
        return '/';
      case 'diagonal':
        return '#';
      case 'tolerance':
        return '^';
    }
  }

  // Compact empty first paragraph if needed
  const cleaned = paragraphs.filter((p) => p.runs.length > 0);

  // Build plain text
  const plain = cleaned
    .map((p) =>
      p.runs.map((r) => (r.kind === 'text' ? r.text : `${r.top}${dividerChar(r.divider)}${r.bottom}`)).join(''),
    )
    .join('\n');

  return { paragraphs: cleaned.length ? cleaned : paragraphs, plain };
}

// ---------- utils ----------

function cloneStyle(s: Style): Style {
  return {
    underline: s.underline,
    overline: s.overline,
    strike: s.strike,
    colorIndex: s.colorIndex,
    height: s.height,
    heightRel: s.heightRel,
    widthFactor: s.widthFactor,
    widthFactorRel: s.widthFactorRel,
    tracking: s.tracking,
    trackingRel: s.trackingRel,
    oblique: s.oblique,
    font: s.font ? { ...s.font } : undefined,
  };
}

function shallowStyleEq(a: Style, b: Style): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
